import React from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

interface DetectIncomeDialogProps {
  open: boolean
  isDetecting: boolean
  // Set after a request failed; the dialog stays open so it can be retried or backed out of.
  error?: string
  onCancel: () => void
  onConfirm: () => void
}

// Detection re-runs FHS over the customer's transactions and can replace their primary income, so
// it is confirmed rather than fired on a click.
export const DetectIncomeDialog: React.FC<DetectIncomeDialogProps> = ({
  open,
  isDetecting,
  error,
  onCancel,
  onConfirm,
}) => (
  <Dialog
    open={open}
    // Closing mid-request would hide the outcome, so the dialog stays until it settles.
    onClose={isDetecting ? undefined : onCancel}
    maxWidth="xs"
    fullWidth
    aria-labelledby="detect-income-title"
    sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
  >
    <DialogTitle id="detect-income-title" sx={{ fontSize: '18px', fontWeight: 600 }}>
      Detect income
    </DialogTitle>

    <DialogContent>
      <Typography sx={{ fontSize: '14px' }}>
        Invoke FHS to detect the latest income for this user?
      </Typography>
      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 1 }}>
        Detection runs in the background, and results can take a few minutes to appear.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          The request failed: {error}
        </Alert>
      )}
    </DialogContent>

    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} disabled={isDetecting}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={isDetecting}
        startIcon={isDetecting ? <CircularProgress size={14} color="inherit" /> : undefined}
        sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
      >
        {error ? 'Try again' : 'Detect Income'}
      </Button>
    </DialogActions>
  </Dialog>
)
